import { useState } from 'react'
import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { Container } from '@magnetic/container'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Text } from '@magnetic/text'
import { Card } from '@magnetic/card'
import { Tabs } from '@magnetic/tabs'
import { Button } from '@magnetic/button'
import { Modal } from '@magnetic/modal'
import { Spinner } from '@magnetic/spinner'
import { Row, Col } from '@magnetic/grid'
import { UsersTable } from '@/components/users-table'
import { ActiveSessionsTable } from '@/components/active-sessions-table'
import {
  listUsers,
  getUserStatistics,
  listActiveSessions,
  revokeSession,
  revokeUserSessions,
} from '@/lib/api'
import { invalidateAdminUsersTables } from '@/lib/connect-query-invalidation'
import { useMutationWithToast } from '@/hooks/useMutationWithToast'
import { Users, Activity, ShieldCheck, Clock, Trash, XCircle } from '@/lib/icons'

export function AdminUsersPage() {
  const [activeTab, setActiveTab] = useState('users')
  const [confirmAction, setConfirmAction] = useState<{
    type: 'revoke-session' | 'revoke-user-sessions'
    sessionId?: string
    userId?: string
    userEmail?: string
  } | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  // Fetch user statistics
  const { data: statsData, isLoading: statsLoading } = useConnectQuery(
    getUserStatistics,
    {},
    {
      refetchInterval: 60000, // Refresh every minute
    }
  )

  const stats = (statsData as { statistics?: { totalUsers: number; activeUsers_30D: number; totalSessions: number; superAdmins: number } })?.statistics

  // Revoke session mutation
  const revokeSessionMutation = useMutationWithToast(revokeSession, {
    successTitle: 'Session revoked',
    successMessage: 'The user session has been revoked successfully',
    errorTitle: 'Failed to revoke session',
    invalidate: invalidateAdminUsersTables,
  })

  // Revoke all user sessions mutation
  const revokeUserSessionsMutation = useMutationWithToast<{ revokedCount?: number }>(revokeUserSessions, {
    successTitle: 'Sessions revoked',
    successMessage: (data) => `Revoked ${data.revokedCount || 0} session(s) for the user`,
    errorTitle: 'Failed to revoke sessions',
    invalidate: invalidateAdminUsersTables,
  })

  const handleRevokeSession = (sessionId: string) => {
    setConfirmAction({ type: 'revoke-session', sessionId })
  }

  const handleConfirmAction = async () => {
    if (!confirmAction) return

    setIsProcessing(true)
    try {
      if (confirmAction.type === 'revoke-session' && confirmAction.sessionId) {
        await revokeSessionMutation.mutateAsync({ sessionId: confirmAction.sessionId })
      } else if (confirmAction.type === 'revoke-user-sessions' && confirmAction.userId) {
        await revokeUserSessionsMutation.mutateAsync({ userId: confirmAction.userId })
      }
      setConfirmAction(null)
    } finally {
      setIsProcessing(false)
    }
  }


  return (
    <Container>
      <Flex direction="vertical" gap={24} style={{ padding: '24px 0' }}>
        {/* Page Header */}
        <Flex direction="vertical" gap={4}>
          <Flex align="center" gap={8}>
            <Users size={24} weight="bold" />
            <Heading size="h1">Users</Heading>
          </Flex>
          <Text color="light">
            Manage users, sessions, and access control
          </Text>
        </Flex>

        {/* Statistics Cards */}
        {statsLoading ? (
          <Flex align="center" justify="center" style={{ padding: 24 }}>
            <Spinner size="md" />
          </Flex>
        ) : stats ? (
          <Row>
            <Col sm={12} md={6} span={3}>
              <Card style={{ boxShadow: 'none', height: '100%' }}>
                <Flex direction="vertical" gap={8}>
                  <Flex align="center" gap={8}>
                    <Users size={20} weight="bold" style={{ color: 'var(--mds-color-theme-text-information-normal)' }} />
                    <Text size="p3" color="light">Total Users</Text>
                  </Flex>
                  <Heading size="h2">{stats.totalUsers || 0}</Heading>
                </Flex>
              </Card>
            </Col>
            <Col sm={12} md={6} span={3}>
              <Card style={{ boxShadow: 'none', height: '100%' }}>
                <Flex direction="vertical" gap={8}>
                  <Flex align="center" gap={8}>
                    <Activity size={20} weight="bold" style={{ color: 'var(--mds-color-theme-text-positive-normal)' }} />
                    <Text size="p3" color="light">Active Users (30d)</Text>
                  </Flex>
                  <Heading size="h2">{stats.activeUsers_30D || 0}</Heading>
                </Flex>
              </Card>
            </Col>
            <Col sm={12} md={6} span={3}>
              <Card style={{ boxShadow: 'none', height: '100%' }}>
                <Flex direction="vertical" gap={8}>
                  <Flex align="center" gap={8}>
                    <ShieldCheck size={20} weight="bold" style={{ color: 'var(--mds-color-theme-text-warning-normal)' }} />
                    <Text size="p3" color="light">Super Admins</Text>
                  </Flex>
                  <Heading size="h2">{stats.superAdmins || 0}</Heading>
                </Flex>
              </Card>
            </Col>
            <Col sm={12} md={6} span={3}>
              <Card style={{ boxShadow: 'none', height: '100%' }}>
                <Flex direction="vertical" gap={8}>
                  <Flex align="center" gap={8}>
                    <Clock size={20} weight="bold" style={{ color: 'var(--mds-color-theme-text-progressing-normal)' }} />
                    <Text size="p3" color="light">Active Sessions</Text>
                  </Flex>
                  <Heading size="h2">{stats.totalSessions || 0}</Heading>
                </Flex>
              </Card>
            </Col>
          </Row>
        ) : null}

        {/* Tabs */}
        <Card style={{ boxShadow: 'none' }}>
          <Tabs>
            <Tabs.Link
              href="#users"
              selected={activeTab === 'users'}
              onClick={(e) => {
                e.preventDefault()
                setActiveTab('users')
              }}
            >
              All Users
            </Tabs.Link>
            <Tabs.Link
              href="#sessions"
              selected={activeTab === 'sessions'}
              onClick={(e) => {
                e.preventDefault()
                setActiveTab('sessions')
              }}
            >
              Active Sessions
            </Tabs.Link>
          </Tabs>

          <Flex style={{ padding: '24px 0' }}>
            {activeTab === 'users' && (
              <UsersTable
                queryFn={listUsers}
                description="Manage all users who have signed in to the platform"
              />
            )}
            {activeTab === 'sessions' && (
              <ActiveSessionsTable
                queryFn={listActiveSessions}
                description="Monitor and manage active user sessions"
                renderActions={(session) => (
                  <Button
                    kind="tertiary"
                    size="sm"
                    icon={<Trash size={16} weight="bold" />}
                    onClick={() => handleRevokeSession(session.sessionId)}
                  >
                    Revoke
                  </Button>
                )}
              />
            )}
          </Flex>
        </Card>
      </Flex>

      {/* Confirmation Modal */}
      {confirmAction && (
        <Modal
          isOpen={true}
          onClose={() => !isProcessing && setConfirmAction(null)}
          size="md"
        >
          <Modal.Content>
            <Flex direction="vertical" gap={24} style={{ padding: 24 }}>
              <Heading size="section">
                {confirmAction.type === 'revoke-session'
                  ? 'Revoke Session'
                  : 'Revoke All User Sessions'}
              </Heading>

              <Flex align="center" gap={12}>
                <XCircle size={48} style={{ color: 'var(--mds-color-theme-text-negative-normal)' }} />
                <Flex direction="vertical" gap={4}>
                  <Text size="p2" weight="semi-bold">
                    {confirmAction.type === 'revoke-session'
                      ? 'Are you sure you want to revoke this session?'
                      : `Are you sure you want to revoke all sessions for ${confirmAction.userEmail}?`}
                  </Text>
                  <Text size="p3" color="light">
                    {confirmAction.type === 'revoke-session'
                      ? 'The user will be signed out immediately and will need to sign in again.'
                      : 'The user will be signed out from all devices and will need to sign in again.'}
                  </Text>
                </Flex>
              </Flex>

              <Flex justify="flex-end" gap={12}>
                <Button
                  kind="secondary"
                  onClick={() => setConfirmAction(null)}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button
                  kind="negative"
                  onClick={handleConfirmAction}
                  disabled={isProcessing}
                  icon={isProcessing ? <Spinner size="sm" /> : <Trash size={16} weight="bold" />}
                >
                  {isProcessing ? 'Revoking...' : 'Revoke'}
                </Button>
              </Flex>
            </Flex>
          </Modal.Content>
        </Modal>
      )}
    </Container>
  )
}
